/**
 * Who sits at a blackjack table, and who deals — chosen per sitting by the DRIVER's
 * generator, from the sitting's seed.
 *
 * The room used a hard-coded `BOT_NAMES` list ("Amos", "Della", "Ruth", "Josiah") and
 * no faces. The seats are the cast now — the same 28 invented people the poker table
 * seats, through `roster.ts` and `casting.ts` — and the rules are poker's:
 *
 *   - never the player's own persona (Phase 6, box 0(3)): `castFaces` moves a seat that
 *     would wear it onto the next face of the same tier;
 *   - never a DEALER's face at a seat: the two dealers are the house, and a player
 *     looking up at the dealer's face on the next chair would be looking at a mistake;
 *   - the dealer is `dealer-m` or `dealer-f`, one per sitting. Their names are the ones
 *     the bj01 mock gave them (`bj01-emmett.jpg`, `bj01-delphine.jpg`).
 *
 * The generator is `driverRng`, seeded from the sitting's seed under its own label, so
 * the engine's streams are never drawn from and the same seed seats the same table.
 */
import { CAST } from "@engine/cast";
import { rosterForName, DEALERS } from "../art/roster";
import { castFaces } from "../art/casting";
import { driverRng, seedFromString } from "./director";

export const DEALER_NAMES: Record<(typeof DEALERS)[number], string> = {
  "dealer-m": "Emmett",
  "dealer-f": "Delphine",
};

export interface BjCast {
  /** Short names, by seat; the player's is "You". */
  names: string[];
  /** Full cast names, by seat (the player's empty) — what the roster keys on. */
  fullNames: string[];
  /** Seat -> roster slug, for every seat but the player's. */
  faces: Record<number, string>;
  dealer: { name: string; slug: string };
}

export function blackjackCast(seedHex: string, seats: number, persona: string | null): BjCast {
  const rng = driverRng(seedFromString(`${seedHex}/blackjack-cast`));
  const pool = CAST.filter((c) => {
    const slug = rosterForName(c.name)?.slug;
    return slug && !(DEALERS as readonly string[]).includes(slug);
  });
  const picked: typeof pool = [];
  const left = [...pool];
  for (let i = 1; i < seats && left.length; i++) {
    const k = Math.floor(rng() * left.length);
    picked.push(left.splice(k, 1)[0]!);
  }
  const fullNames = ["", ...picked.map((c) => c.name)];
  const names = ["You", ...picked.map((c) => c.short)];
  const faces = castFaces(fullNames.map((n, i) => (i === 0 ? "You" : n)), 0, persona);
  const slug = DEALERS[Math.floor(rng() * DEALERS.length)]!;
  return { names, fullNames, faces, dealer: { name: DEALER_NAMES[slug], slug } };
}
